import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { RefreshCw, Eye, Image as ImageIcon, X } from "react-feather";
import { useSelector } from "react-redux";
import { getOrderFeedback, getFeedbackSubjects, getFeedbackStats } from "../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader as RLoader } from "../reporting/ReportingShared";

const PLACEHOLDER_IMG =
    "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png";

const filterInputStyle = {
    padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`,
    fontFamily: "inherit", outline: "none", color: C.text, background: C.white,
};
const filterSelectStyle = { ...filterInputStyle, cursor: "pointer", minWidth: 130 };
const pageBtn = (disabled) => ({
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
    color: disabled ? C.textLight : C.textMed, padding: "6px 14px",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});

const hasPhoto = (feedback) => !!feedback?.image;
const imageUrl = (feedback) => (hasPhoto(feedback) ? process.env.REACT_APP_URL + feedback.image : PLACEHOLDER_IMG);

const customerName = (feedback) => {
    const linked = `${feedback?.User?.firstName || ""} ${feedback?.User?.lastName || ""}`.trim();
    return feedback?.customerName || linked || "—";
};
const customerPhone = (feedback) => feedback?.customerPhone || feedback?.User?.phoneNo || "—";

const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};

const timeAgo = (value) => {
    if (!value) return "";
    const seconds = Math.floor((Date.now() - new Date(value)) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? "s" : ""} ago`;
};

// ─── Small presentational pieces ───────────────────────────
const InfoBadge = ({ label, value }) => (
    <span style={{
        display: "inline-block", padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
        background: C.bg, color: C.textMed, marginRight: 4, marginBottom: 4, border: `1px solid ${C.borderLight}`,
    }}>{label}: {value}</span>
);

const ActionIcon = ({ icon: Icon, color, title, onClick }) => (
    <div onClick={onClick} title={title}
        style={{
            width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", transition: "all 0.15s",
            border: `1px solid ${C.border}`, background: C.white,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.coralLight; e.currentTarget.style.borderColor = C.coral; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; }}>
        <Icon size={15} color={color} />
    </div>
);

const DetailRow = ({ icon, label, value }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.borderLight}` }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: C.coralLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className={`fa fa-${icon}`} style={{ color: C.coral, fontSize: 14 }} />
        </div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 2, wordBreak: "break-word" }}>{value || "—"}</div>
        </div>
    </div>
);

const EMPTY_FILTERS = { user: "", subjectId: "", hasImage: "", search: "", startDate: "", endDate: "" };

const FeedbackTable = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [feedbacks, setFeedbacks] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [stats, setStats] = useState(null);
    const [selected, setSelected] = useState(null);

    // `draft` is what the filter bar shows; `applied` is what the request uses, so
    // typing in the search box doesn't fire a request per keystroke.
    const [draft, setDraft] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const merchantParam = selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : "";

    const fetchFeedbacks = useCallback(async () => {
        setIsLoading(true);
        const resData = await getOrderFeedback({ ...applied, merchant: merchantParam, page, limit });
        const data = resData?.data || {};
        setFeedbacks(data?.feedbacks || []);
        setTotalPages(data?.totalPages || 1);
        setCurrentPage(data?.currentPage || 1);
        setTotalRecords(data?.totalRecords || 0);
        setIsLoading(false);
    }, [applied, merchantParam, page, limit]);

    useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);

    // Deliberately not keyed on page/limit — the totals cover every matching row,
    // so paging through results doesn't need to recompute them.
    const fetchStats = useCallback(async () => {
        const data = await getFeedbackStats({ ...applied, merchant: merchantParam });
        setStats(data);
    }, [applied, merchantParam]);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    useEffect(() => {
        const loadSubjects = async () => {
            if (!merchantParam) { setSubjects([]); return; }
            const list = await getFeedbackSubjects(merchantParam);
            setSubjects(Array.isArray(list) ? list : []);
        };
        loadSubjects();
    }, [merchantParam]);

    // Switching merchant invalidates the subject and customer filters.
    useEffect(() => {
        setDraft(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        setPage(1);
    }, [merchantParam]);

    const setDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

    const applyFilters = (overrides = {}) => {
        const next = { ...draft, ...overrides };
        setDraft(next);
        setApplied(next);
        setPage(1);
    };

    const clearFilters = () => {
        setDraft(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        setPage(1);
    };

    const activeFilterCount = Object.values(applied).filter((v) => v !== "" && v !== null).length;
    const scopeLabel = activeFilterCount > 0 ? "matching filters" : "all time";

    // Totals come from the stats endpoint and cover every matching row, not just
    // the page on screen. `totalRecords` is the same number from the list call.
    const latest = stats?.latestAt;
    const topSubject = stats?.bySubject?.[0];

    return (
        <>
            <style>{reportingGlobalCSS}</style>
            <div className="rpt-wrap">

                {/* ─── KPI Tiles ─────────────────────────────── */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                    <Tile
                        label="Total Feedback"
                        value={stats?.total ?? totalRecords}
                        sub={topSubject ? `Top: ${topSubject.title} (${topSubject.count})` : scopeLabel}
                        icon="💬"
                        gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`}
                        delay={0}
                    />
                    <Tile
                        label="With Photo"
                        value={stats?.withPhoto ?? "—"}
                        sub={stats ? `${stats.withoutPhoto} without` : scopeLabel}
                        icon="📷"
                        gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`}
                        delay={60}
                    />
                    <Tile
                        label="Customers"
                        value={stats?.uniqueCustomers ?? "—"}
                        sub={stats?.guestFeedback ? `+ ${stats.guestFeedback} guest` : "unique"}
                        icon="👤"
                        gradient={`linear-gradient(135deg, ${C.purple}, #C39BD3)`}
                        delay={120}
                    />
                    <Tile
                        label="Latest"
                        value={latest ? timeAgo(latest) : "—"}
                        sub={latest ? formatDateTime(latest) : "no feedback yet"}
                        icon="🕒"
                        gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`}
                        delay={180}
                    />
                </div>

                {/* ─── Filter Bar + Table ────────────────────── */}
                <RCard>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                        <div onClick={() => { fetchFeedbacks(); fetchStats(); }} title="Refresh"
                            style={{
                                width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, display: "flex",
                                alignItems: "center", justifyContent: "center", cursor: "pointer",
                                transition: "all 0.15s", background: C.white, flexShrink: 0,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.coral; e.currentTarget.style.background = C.coralLight; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}>
                            <RefreshCw size={15} color={C.textMed} />
                        </div>

                        <select style={filterSelectStyle} value={draft.subjectId}
                            onChange={(e) => applyFilters({ subjectId: e.target.value })}>
                            <option value="">All Subjects</option>
                            {subjects.map((s) => (
                                <option key={s.id} value={s.id}>{s.title}</option>
                            ))}
                        </select>

                        <select style={filterSelectStyle} value={draft.hasImage}
                            onChange={(e) => applyFilters({ hasImage: e.target.value })}>
                            <option value="">Photo: Any</option>
                            <option value="true">With Photo</option>
                            <option value="false">Without Photo</option>
                        </select>

                        <input type="date" style={filterInputStyle} value={draft.startDate} title="From date"
                            onChange={(e) => applyFilters({ startDate: e.target.value })} />
                        <input type="date" style={filterInputStyle} value={draft.endDate} title="To date"
                            onChange={(e) => applyFilters({ endDate: e.target.value })} />

                        <input
                            style={{ ...filterInputStyle, width: 220 }}
                            placeholder="Search name, phone or text"
                            value={draft.search}
                            onChange={(e) => setDraftField("search", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                            onBlur={() => { if (draft.search !== applied.search) applyFilters(); }}
                        />

                        {activeFilterCount > 0 && (
                            <button onClick={clearFilters} title="Clear all filters"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 6, background: C.coralLight,
                                    border: `1px solid ${C.coral}`, color: C.coral, borderRadius: 8,
                                    fontWeight: 600, fontSize: 12, padding: "6px 12px", cursor: "pointer",
                                }}>
                                <X size={13} /> Clear ({activeFilterCount})
                            </button>
                        )}

                        <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>
                            {totalRecords} feedback{totalRecords !== 1 ? "s" : ""}
                        </span>
                    </div>

                    {applied.user && (
                        <div style={{ marginBottom: 12 }}>
                            <span style={{
                                display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px",
                                borderRadius: 20, fontSize: 11, fontWeight: 700, background: C.purpleLight, color: C.purple,
                            }}>
                                Customer #{applied.user}
                                <X size={13} style={{ cursor: "pointer" }} onClick={() => applyFilters({ user: "" })} />
                            </span>
                        </div>
                    )}

                    {isLoading ? <RLoader /> : (
                        <>
                            <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>ID</th>
                                            <th style={tbl.th}>Photo</th>
                                            <th style={tbl.th}>Customer</th>
                                            <th style={tbl.th}>Subject</th>
                                            <th style={{ ...tbl.th, minWidth: 260 }}>Feedback</th>
                                            <th style={tbl.th}>Submitted</th>
                                            <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {feedbacks.length > 0 ? feedbacks.map((feedback) => (
                                            <tr key={feedback.id} className="rpt-row">
                                                <td style={{ ...tbl.td, color: C.textLight, fontWeight: 600 }}>{feedback.id}</td>
                                                <td style={tbl.td}>
                                                    {hasPhoto(feedback) ? (
                                                        <img
                                                            alt="Feedback attachment"
                                                            src={imageUrl(feedback)}
                                                            onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                                                            onClick={() => setSelected(feedback)}
                                                            style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", border: `1px solid ${C.border}`, cursor: "pointer" }}
                                                        />
                                                    ) : (
                                                        <div style={{ width: 44, height: 44, borderRadius: 8, background: C.bg, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <ImageIcon size={16} color={C.textLight} />
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={tbl.td}>
                                                    <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>{customerName(feedback)}</div>
                                                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                                                        <InfoBadge label="Phone" value={customerPhone(feedback)} />
                                                        <InfoBadge label="Order" value={feedback.orderId} />
                                                        {feedback.userId && (
                                                            <span
                                                                onClick={() => applyFilters({ user: String(feedback.userId) })}
                                                                title="Filter by this customer"
                                                                style={{
                                                                    display: "inline-block", padding: "3px 8px", borderRadius: 6, fontSize: 10,
                                                                    fontWeight: 700, background: C.coralLight, color: C.coral,
                                                                    marginRight: 4, marginBottom: 4, border: `1px solid ${C.coralLight}`, cursor: "pointer",
                                                                }}>
                                                                Customer: {feedback.userId}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={tbl.td}>
                                                    {feedback.subject?.title
                                                        ? <Badge color={C.coral} bg={C.coralLight}>{feedback.subject.title}</Badge>
                                                        : <span style={{ color: C.textLight }}>—</span>}
                                                </td>
                                                <td style={{ ...tbl.td, color: C.textMed, maxWidth: 380 }}>
                                                    <div style={{
                                                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                                        overflow: "hidden", lineHeight: 1.5,
                                                    }} title={feedback.description}>
                                                        {feedback.description || "—"}
                                                    </div>
                                                </td>
                                                <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                                                    <div style={{ color: C.text, fontWeight: 600 }}>{formatDateTime(feedback.createdAt)}</div>
                                                    <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{timeAgo(feedback.createdAt)}</div>
                                                </td>
                                                <td style={tbl.td}>
                                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                                        <ActionIcon icon={Eye} color={C.coral} title="View Details" onClick={() => setSelected(feedback)} />
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="7" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                                                    {activeFilterCount > 0 ? "No feedback matches these filters." : "No feedback submitted yet."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 12, color: C.textLight }}>
                                    Page {currentPage} / {totalPages || 1} · {feedbacks.length} of {totalRecords} shown
                                </span>
                                <select
                                    style={{ ...filterSelectStyle, minWidth: 110 }}
                                    value={limit}
                                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                                >
                                    {[10, 25, 50, 100].map((n) => (
                                        <option key={n} value={n}>{n} per page</option>
                                    ))}
                                </select>
                                <div style={{ flex: 1 }} />
                                <button style={pageBtn(currentPage <= 1)} disabled={currentPage <= 1}
                                    onClick={() => setPage(currentPage - 1)}>Prev</button>
                                <button style={pageBtn(currentPage >= totalPages)} disabled={currentPage >= totalPages}
                                    onClick={() => setPage(currentPage + 1)}>Next</button>
                            </div>
                        </>
                    )}
                </RCard>
            </div>

            {/* ─── Feedback Detail Modal ─────────────────────── */}
            <Modal isOpen={!!selected} toggle={() => setSelected(null)} size="lg" centered>
                <ModalHeader toggle={() => setSelected(null)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
                    <span style={{ fontWeight: 700, color: C.text }}>Feedback #{selected?.id}</span>
                </ModalHeader>
                {selected && (
                    <ModalBody style={{ padding: 24, fontFamily: "'Inter', sans-serif" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                            <div>
                                <DetailRow icon="user" label="Customer Name" value={customerName(selected)} />
                                <DetailRow icon="phone" label="Phone Number" value={customerPhone(selected)} />
                                <DetailRow icon="envelope" label="Email" value={selected.User?.email} />
                                <DetailRow icon="id-badge" label="Customer ID" value={selected.userId} />
                            </div>
                            <div>
                                <DetailRow icon="tag" label="Subject" value={selected.subject?.title} />
                                <DetailRow icon="shopping-bag" label="Order ID" value={selected.orderId} />
                                <DetailRow icon="clock-o" label="Submitted" value={`${formatDateTime(selected.createdAt)} (${timeAgo(selected.createdAt)})`} />
                                <DetailRow icon="building" label="Merchant ID" value={selected.merchantId ?? selected.Order?.MerchantId} />
                            </div>
                        </div>

                        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `2px solid ${C.borderLight}` }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>Description</div>
                            <div style={{
                                background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16,
                                fontSize: 13, color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap",
                            }}>
                                {selected.description || "No description provided."}
                            </div>
                        </div>

                        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `2px solid ${C.borderLight}` }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>Attachment</div>
                            {hasPhoto(selected) ? (
                                <a href={imageUrl(selected)} target="_blank" rel="noopener noreferrer">
                                    <img
                                        alt="Feedback attachment"
                                        src={imageUrl(selected)}
                                        onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                                        style={{ maxWidth: "100%", maxHeight: 340, borderRadius: 10, border: `1px solid ${C.border}` }}
                                    />
                                </a>
                            ) : (
                                <div style={{ color: C.textLight, fontSize: 13 }}>No photo attached.</div>
                            )}
                        </div>
                    </ModalBody>
                )}
                <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}` }}>
                    <Button
                        style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }}
                        onClick={() => setSelected(null)}
                    >Close</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default FeedbackTable;
